import React, { Component, Fragment } from 'react';

import { Loading } from 'components/cosmos';
import { Checkbox } from 'components/cosmos/checkbox';
import { Tabs } from 'components/tabs';
import { I18n, translate } from 'components/translate';
import { ROUTES, PAGE_VIEW_EVENTS } from 'configs/routes/constants';
import { showSurvey, SURVEY_ID_LIST } from 'configs/Survicate';
import { rawTextLength } from 'helpers/String';
import { inject, observer, PropTypes as MobxPropTypes } from 'mobx-react';
import { ArrivedFrom, DatahubEventsService } from 'modules/app/services';
import ViewContentCreateSchema from 'modules/app/services/datahubEvents/module/viewContentCreateSchema';
import ViewContentEditSchema from 'modules/app/services/datahubEvents/module/viewContentEditSchema';
import { ContentService, ContentPageService } from 'modules/content';
import ContentBasePage from 'modules/content/components/ContentBasePage';
import { Rerender } from 'modules/content/components/reRender';
import {
  CONTENT_PAGE_TYPE,
  getContentPageTitle
} from 'modules/content/contentPageTitles';
import { DrippingStore } from 'modules/content/dripping/stores';
import { PageType } from 'modules/content/moduleService/ModuleTypes';
import { QuizService } from 'modules/content/quiz';
import { UiService } from 'modules/ui';
import PropTypes from 'prop-types';
import QueryString from 'query-string';

import QuizConfigs from './QuizConfigs';
import { QuizControl } from './quizControl';
import QuizProfiles from './QuizProfiles';
import QuizQuestions from './QuizQuestions';

import ContentPreventLeave from '../../components/ContentPreventLeave';
import DrippingValidation from '../../components/dripping/DrippingValidation';

import './Quiz.scss';

@translate()
@inject((stores) => ({
  QuizStore: stores.QuizStore,
  isOnlineEventTemplate: stores.MembershipStore.isOnlineEventTemplate,
  isMobile: stores.UiStore.isMobile,
  hasDuration: stores.QuizStore.page?.hasDuration,
  liberationType: stores.QuizStore.page?.liberationType,
  liberationDays: stores.QuizStore.page?.liberationDays,
  liberationDate: stores.QuizStore.page?.liberationDate,
  durationDays: stores.QuizStore.page?.durationDays,
  isDrippingDirty: stores.DrippingStore.isDirty,
  hasExpiration: stores.DrippingStore.hasExpiration
}))
@observer
export default class Quiz extends Component {
  static propTypes = {
    match: PropTypes.shape({
      params: PropTypes.shape({
        moduleId: PropTypes.string,
        pageId: PropTypes.string,
        id: PropTypes.id
      })
    }),
    t: PropTypes.func,
    page: PropTypes.object,
    type: PropTypes.string,
    QuizStore: MobxPropTypes.objectOrObservableObject,
    history: PropTypes.object,
    maxDescriptionLen: PropTypes.number,
    isOnlineEventTemplate: PropTypes.bool,
    isMobile: PropTypes.bool,
    hasDuration: PropTypes.bool,
    liberationType: PropTypes.string,
    liberationDays: PropTypes.number,
    liberationDate: PropTypes.instanceOf(Date),
    durationDays: PropTypes.number
  };

  static defaultProps = {
    maxDescriptionLen: 400
  };

  state = {
    loaded: false,
    currentTab: 0,
    isSaving: false,
    fieldErrorName: false,
    languageError: false,
    drippingErrors: DrippingValidation.initialState
  };

  constructor(props) {
    super(props);

    this.reRenderRef = React.createRef();

    this.handleCreateAnotherChange.bind(this);
  }

  get tabs() {
    return [
      { name: this.props.t('quiz_editor.tabs.name_and_type') },
      { name: this.props.t('quiz_editor.tabs.profiles'), track: 'Profiles' },
      { name: this.props.t('quiz_editor.tabs.questions'), track: 'Questions' }
    ];
  }

  get seasonId() {
    const qs = QueryString.parse(location.search);

    return qs.season ? qs.season : null;
  }

  get extra() {
    const qs = QueryString.parse(location.search);

    return qs.extra ? qs.extra : false;
  }

  get actions() {
    const actions = {};

    if (QuizService.quizId) {
      actions.onPublish = this.publish.bind(this);
      actions.onVisualize = this.visualize.bind(this);
    }

    return actions;
  }

  get renderPreventLeave() {
    const { snapshot } = this.props.QuizStore;
    const { isDrippingDirty } = this.props;

    return (
      <Fragment>
        <ContentPreventLeave
          snapshot={snapshot}
          current={QuizService.current}
          isForceDirty={isDrippingDirty}
        />
      </Fragment>
    );
  }

  componentDidMount() {
    this.fetchData();
    this.dispatchDatahubEvent();
  }

  dispatchDatahubEvent = () => {
    const eventParams = {
      pageType: PageType.Quiz,
      arrivedFrom: ArrivedFrom.CONTENT_LIST
    };

    const schema = this.props.match.params.id
      ? new ViewContentEditSchema(eventParams)
      : new ViewContentCreateSchema(eventParams);

    DatahubEventsService.sendEvent({ schema });
  };

  fetchData = async () => {
    const { params } = this.props.match;

    if (params.id) {
      await QuizService.getQuiz(params.id);
    } else {
      QuizService.moduleId = params.moduleId;
      QuizService.snapshot = QuizService.current;
    }

    ContentService.lastEditedModule = params.moduleId;

    this.toggleLoader();
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    UiService.smoothScroll('body', 500);
  };

  componentWillUnmount() {
    QuizService.clear();
  }

  changeFieldError = (name, state) => {
    this.setState({ [name]: state });
  };

  validateNameAndTypeTab = () => {
    return (
      this.checkEmptyName() &&
      this.checkDescriptionLength() &&
      this.checkLanguage()
    );
  };

  validateTabChange = (index) => {
    switch (index) {
      case 0:
        return true;
      case 1:
        return this.validateNameAndTypeTab() && this.checkConfig();
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      case 2:
        return (
          this.validateNameAndTypeTab() &&
          this.checkConfig() &&
          this.checkTabQuestions()
        );
      default:
        return false;
    }
  };

  checkEmptyName = () => {
    if (!QuizService.name) {
      this.state.currentTab !== 0 && this.setState({ currentTab: 0 });
      UiService.smoothScroll(
        '#name',
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        600,
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        140
      );

      const fieldErrorName = 'fieldErrorName';
      const errorState = true;

      this.changeFieldError(fieldErrorName, errorState);

      return false;
    }

    return true;
  };

  checkTabQuestions = () => {
    if (QuizService.isProfile() && !QuizService.hasProfile()) {
      this.state.currentTab !== 1 && this.setState({ currentTab: 1 });
      UiService.setAlertError('quiz.empty_profile');
      return false;
    } else if (!QuizService.isProfile()) {
      return this.checkEmptyName();
    }
    return true;
  };

  checkDescriptionLength() {
    const { page } = this.props.QuizStore;
    const rawContentLength = rawTextLength(page.content || '');
    const isDescriptionValid = rawContentLength <= this.props.maxDescriptionLen;

    if (!isDescriptionValid) {
      this.state.currentTab !== 0 && this.setState({ currentTab: 0 });
      UiService.setAlertError('quiz.profile_description_limit', {
        limit: this.props.maxDescriptionLen
      });
      UiService.smoothScroll(
        '#description',
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        600,
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        140
      );
    }

    return isDescriptionValid;
  }

  checkDripping() {
    const {
      hasDuration,
      liberationType,
      liberationDays,
      liberationDate,
      durationDays
    } = this.props;
    const drippingValidation = new DrippingValidation(
      hasDuration,
      liberationType
    );
    const drippingErrors = drippingValidation.validate({
      liberationDays,
      liberationDate,
      durationDays
    });

    this.setState({ drippingErrors });

    return !drippingValidation.hasError;
  }

  checkConfig() {
    return this.checkDripping();
  }

  checkLanguage = () => {
    const { isOnlineEventTemplate } = this.props;

    if (isOnlineEventTemplate && !QuizService.language) {
      this.state.currentTab !== 0 && this.setState({ currentTab: 0 });
      UiService.smoothScroll(
        '#language',
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        600,
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        140
      );

      this.changeFieldError('languageError', true);

      return false;
    }

    return true;
  };

  get formIsValid() {
    const nameIsValid = this.checkEmptyName();
    const descriptionIsValid = this.checkDescriptionLength();
    const languageIsValid = this.checkLanguage();
    const questionsIsValid = this.checkTabQuestions();
    const configIsValid = this.checkConfig();

    return (
      nameIsValid &&
      descriptionIsValid &&
      languageIsValid &&
      questionsIsValid &&
      configIsValid
    );
  }

  onTabChange = (index) => {
    if (this.validateTabChange(index)) {
      this.setState({ currentTab: index }, () => {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        UiService.smoothScroll('body', 500);
      });
    }
  };

  handleLoading = (isSaving) => {
    this.setState({ isSaving });
    this.toggleLoader();
  };

  save = async () => {
    if (this.formIsValid) {
      const isSaving = true;

      this.handleLoading(isSaving);

      const save = await QuizService.save();

      this.handleLoading(false);

      if (save) {
        const seasonId = this.seasonId;

        await ContentService.fetchModulesList(seasonId, this.extra);
        showSurvey(SURVEY_ID_LIST.QUIZ);
      }

      return save;
    }
  };

  publish = async () => {
    this.toggleLoader();

    const modules = null;
    const showAlert = false;
    const response = await ContentPageService.publish(
      [QuizService.quizId],
      !QuizService.published,
      modules,
      showAlert
    );

    await ContentService.fetchModulesList(this.seasonId);

    if (response) {
      QuizService.published = !QuizService.published;
    }
    await this.save();
    this.toggleLoader();
  };

  visualize = () => {
    ContentPageService.contentPreview(QuizService.quizId, null, this.seasonId);
  };

  newQuiz = () => {
    this.toggleLoader();

    this.clear();
    this.reRenderRef.current?.reRender();

    this.fetchData();
  };

  saveAndNewQuiz = async () => {
    const response = await this.save();

    if (response) {
      DrippingStore.clear();

      const { isCreateAnotherChecked } = this.state;

      if (isCreateAnotherChecked) {
        this.newQuiz();

        return;
      }

      if (response !== false) {
        this.props.history.push(
          `${ROUTES.CONTENT.LIST}${window.location.search}`
        );
      }
    }
  };

  clear = () => {
    QuizService.clear();

    this.setState({
      currentTab: 0,
      isSaving: false,
      fieldErrorName: false
    });

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    UiService.smoothScroll('body', 500);
  };

  toggleLoader = () => {
    this.setState((prevState) => ({ loaded: !prevState.loaded }));
  };

  back = () => {
    const { history } = this.props;

    history.push(`${ROUTES.CONTENT.LIST}${history.location.search}`);
  };

  handleCreateAnotherChange = ({ target }) => {
    this.setState({ isCreateAnotherChecked: target.checked });
  };

  render() {
    const { type, page } = this.props.QuizStore;
    const { maxDescriptionLen, isMobile, hasExpiration } = this.props;
    const {
      currentTab,
      loaded,
      fieldErrorName,
      languageError,
      drippingErrors,
      isSaving
    } = this.state;
    const { params } = this.props.match;
    const isEditingItem = params?.id;
    const formTitle = getContentPageTitle(
      CONTENT_PAGE_TYPE.QUIZ,
      isEditingItem
    );

    if (!loaded && !isSaving) {
      return <Loading center />;
    }

    return (
      <>
        {isMobile && (
          <QuizControl
            quizType={type}
            currentTab={currentTab}
            onTabChange={this.onTabChange}
          />
        )}

        <ContentBasePage className="content__grid">
          <ContentBasePage.Header title={formTitle}>
            <ContentBasePage.Header.Actions>
              <ContentBasePage.Header.Actions.Default
                isPublished={page.published}
                {...this.actions}
              />
            </ContentBasePage.Header.Actions>
          </ContentBasePage.Header>

          <Rerender ref={this.reRenderRef}>
            <Tabs
              track={
                params.id
                  ? PAGE_VIEW_EVENTS.CONTENT.EDIT_QUIZ
                  : PAGE_VIEW_EVENTS.CONTENT.NEW_QUIZ
              }
              tabs={this.tabs}
              hide={type === 'EXERCISE' ? 1 : null}
              actived={currentTab}
              onTabChange={this.onTabChange}
              className="quiz _overflow-y-scroll"
            >
              <QuizConfigs
                maxDescriptionLen={maxDescriptionLen}
                fieldErrorName={fieldErrorName}
                changeFieldError={this.changeFieldError}
                languageError={languageError}
                drippingErrors={drippingErrors}
              />
              <QuizProfiles />
              <QuizQuestions
                currentTab={currentTab}
                hasExpiration={hasExpiration}
              />
            </Tabs>
          </Rerender>

          <ContentBasePage.Footer
            onSubmitAndReturn={this.saveAndNewQuiz}
            cancelHandler={this.back}
            isSubmitDisabled={isSaving}
          >
            {!isEditingItem && (
              <Checkbox
                id="createAnotherField"
                name="createAnotherField"
                label={<I18n label="quiz_editor.create_another" />}
                className="_mb-0"
                onChange={this.handleCreateAnotherChange}
              />
            )}

            {!isMobile && (
              <QuizControl
                quizType={type}
                currentTab={currentTab}
                className="_ml-lg-4"
                onTabChange={this.onTabChange}
              />
            )}
          </ContentBasePage.Footer>

          {this.renderPreventLeave}
        </ContentBasePage>
      </>
    );
  }
}
